import React from 'react';
import { ArrowUpRightIcon, CheckIcon } from 'lucide-react';
import { Container } from '../Container';
import { ButtonLink } from '../ButtonLink';
import { Reveal } from '../Reveal';
import { AppImage } from '../AppImage';
import { MockFrame, mockTokens } from './MockFrame';
import { servicePillarImages } from '../../data/servicesImages';
import type { ServiceDetail } from '../../types/content';
interface ServicePillarProps {
  service: ServiceDetail;
  tone: 'canvas' | 'surface';
}
export function ServicePillar({
  service,
  tone
}: ServicePillarProps) {
  const backgroundClass = tone === 'surface' ? 'bg-surface' : 'bg-canvas';
  const isReversed = Number(service.index) % 2 === 0;
  const image = servicePillarImages[service.index];
  return <section aria-labelledby={`service-${service.index}`} className={`border-b border-hairline py-section-sm lg:py-section ${backgroundClass}`} data-id="element-795" data-mp="19gmwe6xjumcf">
      <Container data-id="element-796" data-mp="33sju2hml4ofx">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-10" data-id="element-797" data-mp="33u4lvzhilta3">
          <div className={`lg:col-span-4 ${isReversed ? 'lg:col-start-9 lg:row-start-1' : 'lg:col-start-1'}`} data-id="element-798" data-mp="2tqb9hl94q3b8">
            <div className="lg:sticky lg:top-[112px]" data-id="element-799" data-mp="2xqbob7054git">
              <Reveal data-id="element-800" data-mp="2jtoq70wgbkbb">
                <p className="font-serif text-[48px] leading-none tracking-[-0.04em] text-muted" data-id="element-801" data-mp="2ju7x7mkkcoak">
                  {service.index}
                </p>
                <h2 id={`service-${service.index}`} className="mt-5 max-w-[12ch] text-[40px] leading-[1.03] tracking-[-0.035em] text-ink lg:text-[56px]" data-id="element-802" data-mp="3chkg9mrimdz4">
                  {service.name}
                </h2>
                <p className="mt-7 max-w-[38ch] text-body text-muted" data-id="element-803" data-mp="32petqsk18orb">{service.description}</p>
                <p className="mt-8 text-small text-muted" data-id="element-804" data-mp="37orhrgmn41vh">{service.timeline}</p>
              </Reveal>

              <Reveal delay={0.06} className="mt-10 max-w-[360px]" data-id="element-805" data-mp="3ve3lw2341jqx">
                <MockFrame ratio="4:3" caption={`${service.name} system`} data-id="element-806" data-mp="42amd1xs4f2f"> 
                  {renderMockContent(service.index)}
                </MockFrame>
              </Reveal>
            </div>
          </div>

          <div className={`lg:col-span-7 lg:row-start-1 ${isReversed ? 'lg:col-start-1' : 'lg:col-start-6'}`} data-id="element-807" data-mp="8yxt3yoqglnk">
            <Reveal delay={0.08} data-id="element-808" data-mp="3w3nk0h8lfm8e">
              <AppImage src={image.image} alt={image.alt} ratio="4:3" data-id="element-809" data-mp="3w2kplzc2b1nq" />
            </Reveal>

            <Reveal delay={0.1} className="mt-12" data-id="element-810" data-mp="3r4sxb47i9dj0">
              <p className="text-small font-medium text-ink" data-id="element-811" data-mp="3mdkq8etne490">What&rsquo;s included</p>
              <ul className="mt-6 grid border-t border-hairline sm:grid-cols-2 lg:grid-cols-3" data-id="element-812" data-mp="3mbhtlbxzpcvb">
                {service.deliverables.map(deliverable => <li key={deliverable} className="flex min-h-[70px] items-start gap-3 border-b border-hairline py-5 pr-5 text-small leading-5 text-ink lg:border-r lg:px-5 lg:first:pl-0 lg:[&:nth-child(3n)]:border-r-0" data-id="element-813" data-mp="3cgg4gg3psasq">
                    <CheckIcon aria-hidden="true" size={15} className="mt-0.5 shrink-0 text-accent" data-id="element-814" data-mp="1ivcqia9tvqwg" />
                    <span data-id="element-815" data-mp="1nufxi09dc772">{deliverable}</span>
                  </li>)}
              </ul>
            </Reveal>

            <Reveal delay={0.16} data-id="element-816" data-mp="1rxm3r7whdydj">
              <div className="mt-12 border-t border-hairline pt-7 md:flex md:items-end md:justify-between md:gap-10" data-id="element-817" data-mp="1ww6zov4p9vw6">
                <div data-id="element-818" data-mp="106g6r0c24gsh">
                  <p className="text-small font-medium text-ink" data-id="element-819" data-mp="106y460agh11z">Who it&rsquo;s for</p>
                  <p className="mt-3 max-w-[48ch] text-body text-muted" data-id="element-820" data-mp="146qfgkr8hlb1">{service.audience}</p>
                </div>
                <ButtonLink to="/contact" variant="secondary" className="mt-7 shrink-0 md:mt-0" data-id="element-821" data-mp="195t793i7dv12">
                  Discuss this service
                  <ArrowUpRightIcon aria-hidden="true" size={16} className="ml-2" data-id="element-822" data-mp="1dz5v3bf25xsr" />
                </ButtonLink>
              </div>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>;
}
function renderMockContent(index: string) {
  if (index === '01') {
    return <div className={`grid h-full grid-cols-5 gap-2 rounded-md border border-hairline p-4 ${mockTokens.surface}`} data-id="element-823" data-mp="2o1b9ozja36hx">
        <div className="col-span-2 flex flex-col justify-between" data-id="element-824" data-mp="2wvzgiu4i4w8u">
          <div className={`${mockTokens.heading} ${mockTokens.typeLarge} w-full`} data-id="element-825" data-mp="2wxakatxy63zy" />
          <div className="space-y-2" data-id="element-826" data-mp="2s3ptbluruaa9">
            <div className={`${mockTokens.text} ${mockTokens.typeSmall} w-full`} data-id="element-827" data-mp="36u6ssznuiq4i" />
            <div className={`${mockTokens.text} ${mockTokens.typeSmall} w-3/4`} data-id="element-828" data-mp="36w9re832drku" />
          </div>
        </div>
        <div className="col-span-3 grid grid-cols-2 gap-2" data-id="element-829" data-mp="31xggmnf5weod">
          <div className="rounded-full bg-ink" data-id="element-830" data-mp="3fubo4bfrly2b" />
          <div className={`rounded-md ${mockTokens.accent}`} data-id="element-831" data-mp="3btje3fz2jp4z" />
          <div className="rounded-md bg-[#F1F1EE]" data-id="element-832" data-mp="3bu1hqxo295mx" />
          <div className="rounded-full border border-hairline" data-id="element-833" data-mp="1lgatk2r8zx81" />
        </div>
      </div>;
  }
  if (index === '02') {
    return <div className={`flex h-full flex-col rounded-md border border-hairline p-3 ${mockTokens.surface}`} data-id="element-834" data-mp="1hgtkkzcm4hy4">
        <div className={`flex h-5 items-center gap-1.5 border-b border-hairline pb-2`} data-id="element-835" data-mp="17c3o38zza61a">
          <span className="h-1.5 w-1.5 rounded-full bg-[#DCDCD8]" data-id="element-836" data-mp="1bcdw0r7bz6is" />
          <span className="h-1.5 w-1.5 rounded-full bg-[#DCDCD8]" data-id="element-837" data-mp="xmadaa7ntuy3" />
          <span className="h-1.5 w-1.5 rounded-full bg-[#DCDCD8]" data-id="element-838" data-mp="12klp2qhimfiy" />
        </div>
        <div className="flex flex-1 flex-col justify-center px-3" data-id="element-839" data-mp="12ih8p92fjo1s">
          <div className={`${mockTokens.heading} ${mockTokens.typeLarge} w-4/5`} data-id="element-840" data-mp="nmjkqe2ntq0n" />
          <div className={`mt-3 ${mockTokens.text} ${mockTokens.typeSmall} w-full`} data-id="element-841" data-mp="smnyhwc6o6n6" />
          <div className={`mt-2 ${mockTokens.text} ${mockTokens.typeSmall} w-2/3`} data-id="element-842" data-mp="en66l83lcd9l" />
          <div className={`mt-5 h-6 w-16 rounded-sm ${mockTokens.accent}`} data-id="element-843" data-mp="jlg43pcsfpyd" />
        </div>
      </div>;
  }
  if (index === '03') {
    return <div className={`grid h-full grid-cols-[1fr_1.5fr] gap-3 rounded-md border border-hairline p-4 ${mockTokens.surface}`} data-id="element-844" data-mp="29175tu31z2a7">
        <div className="space-y-2 border-r border-hairline pr-3" data-id="element-845" data-mp="2dxo1raghukxu">
          <div className={`${mockTokens.heading} ${mockTokens.typeSmall} w-3/4`} data-id="element-846" data-mp="2blvdfjgns98p" />
          <div className={`${mockTokens.text} ${mockTokens.typeSmall} w-full`} data-id="element-847" data-mp="27ifjiuvz4mzp" />
          <div className={`${mockTokens.text} ${mockTokens.typeSmall} w-4/5`} data-id="element-848" data-mp="22jvuglm32v34" />
          <div className={`${mockTokens.text} ${mockTokens.typeSmall} w-3/5`} data-id="element-849" data-mp="1xlavsqmhcjgs" />
        </div>
        <div className="grid grid-cols-2 gap-2" data-id="element-850" data-mp="1xnniti73hk0p">
          <div className="rounded-sm border border-hairline p-2" data-id="element-851" data-mp="2v81jvlo3189a">
            <div className={`${mockTokens.accent} h-7 rounded-sm`} data-id="element-852" data-mp="2qa8eguulsia2" />
          </div>
          <div className="rounded-sm border border-hairline p-2" data-id="element-853" data-mp="2lj9v1mebj42l">
            <div className="h-7 rounded-sm bg-[#F1F1EE]" data-id="element-854" data-mp="2lhf7s1fkqzvc" />
          </div>
          <div className="col-span-2 rounded-sm border border-hairline p-2" data-id="element-855" data-mp="2gj5dru5l65bb">
            <svg viewBox="0 0 120 34" className="h-full w-full" aria-hidden="true" data-id="element-856" data-mp="3e02pr7z9xys7">
              <path d="M3 27 L25 18 L43 23 L64 8 L87 16 L117 4" fill="none" stroke="#2F4BFF" strokeWidth="2" data-id="element-857" data-mp="39945xyf2uik7" />
              <path d="M3 30 H117" fill="none" stroke="#E6E6E2" strokeWidth="1" data-id="element-858" data-mp="3gkii4ytabocc" />
            </svg>
          </div>
        </div>
      </div>;
  }
  if (index === '04') {
    return <div className={`flex h-full flex-col rounded-md border border-hairline p-4 ${mockTokens.surface}`} data-id="element-859" data-mp="3qi6hy35qzm1d">
        <div className="grid grid-cols-3 gap-2" data-id="element-860" data-mp="3vgzqdh66gmuo">
          {[0, 1, 2].map(item => <div key={item} className="rounded-sm border border-hairline p-2" data-id="element-861" data-mp="3dgvbwsx77mc">
              <div className={`aspect-square rounded-sm ${item === 1 ? mockTokens.accent : 'bg-[#F1F1EE]'}`} data-id="element-862" data-mp="3be1t2sk6wcv" />
              <div className={`mt-2 ${mockTokens.text} ${mockTokens.typeSmall} w-full`} data-id="element-863" data-mp="d3a3soxrn6md" />
            </div>)}
        </div>
        <div className="mt-auto flex items-center justify-between border-t border-hairline pt-3" data-id="element-864" data-mp="i2mqn9on0doy">
          <div className={`${mockTokens.heading} ${mockTokens.typeSmall} w-1/3`} data-id="element-865" data-mp="n293vj4gttxr" />
          <div className={`h-5 w-12 rounded-sm ${mockTokens.accent}`} data-id="element-866" data-mp="r2ru58qpmrxu" />
        </div>
      </div>;
  }
  return <div className={`flex h-full flex-col rounded-md border border-hairline p-4 ${mockTokens.surface}`} data-id="element-867" data-mp="1cd8ahqk187x7">
      <div className="grid flex-1 grid-cols-5 items-end gap-2" data-id="element-868" data-mp="18bd07wk4ik7a">
        {[32, 52, 44, 70, 88].map((height, item) => <div key={item} className="flex h-full items-end" data-id="element-869" data-mp="1mbnt154zu8v3" data-mpi={item}>
            <div className={`w-full rounded-t-sm ${item === 4 ? mockTokens.accent : 'bg-[#DCDCD8]'}`} style={{
          height: `${height}%`
        }} data-id="element-870" data-mp="1hcl02j2aft3h" />
          </div>)}
      </div>
      <div className="mt-4 border-t border-hairline pt-3" data-id="element-871" data-mp="oofc7nfxtnzf">
        <div className={`${mockTokens.heading} ${mockTokens.typeSmall} w-2/5`} data-id="element-872" data-mp="oq05zaufh2w8" />
        <div className={`mt-2 ${mockTokens.text} ${mockTokens.typeSmall} w-4/5`} data-id="element-873" data-mp="13ed3wdd2svtd" />
      </div>
    </div>;
}